import { useCallback, useEffect, useRef, type FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { FormikProps } from "formik";
import clsx from "clsx";

import { confirm } from "components/Shared/ConfirmDialog";
import { useNavigationGuard } from "providers/NavigationGuard";
import { useIncidentReportContext, type ReportNavigationKey } from "../IncidentReportContextProvider";
import type { ReportMenuItem } from "../reportSections";

import "./ReportNavigation.scss";

const getSectionInfo = (pathname: string, activeSections: ReportMenuItem[]) => {
  const sectionName = pathname.split('/').at(-1);
  const rootPath = sectionName === 'report' ? pathname : pathname.replace(sectionName!, '');
  const currentIndex = sectionName === 'report' ? 0 : activeSections.findIndex(s => s.url === sectionName);
  const currentSection = (activeSections[currentIndex]?.labelKey ?? 'interventionSummary') as ReportNavigationKey;
  return { sectionName, rootPath, currentIndex, currentSection };
};

export const useReportNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hiddenMenuItems, toggleMenuVisibility, reportSections } = useIncidentReportContext();

  const activeSections = reportSections.filter(s => !hiddenMenuItems.includes(s.labelKey));
  const { rootPath, currentIndex, currentSection } = getSectionInfo(location.pathname, activeSections);
  const allMenus = reportSections.map(section => ({ ...section, url: `${rootPath.replace(/\/$/, '')}/${section.url}` }));

  const goToSection = (index: number) => {
    const section = activeSections[index];
    navigate(`${rootPath}/${section.url}`);
  };

  const previousSection = () => {
    if (activeSections[currentIndex]?.labelKey === 'interventionSummary') return;
    goToSection(currentIndex - 1);
  };

  const nextSection = () => {
    if (currentIndex >= activeSections.length - 1) return;
    goToSection(currentIndex + 1);
  };

  const exitReport = () => {
    navigate(rootPath.replace('/report', '').replace(/\/$/, ''));
  };

  const navigateToSection = (labelKey: ReportNavigationKey) => {
    const section = reportSections.find(s => s.labelKey === labelKey);
    if (section) navigate(`${rootPath}/${section.url}`);
  };

  return {
    currentSection,
    allMenus,
    activeSectionNames: activeSections.map(s => s.labelKey),
    isLastSection: currentIndex === activeSections.length - 1,
    previousSection,
    nextSection,
    navigateToSection,
    exitReport,
    toggleMenuVisibility
  };
};

interface ReportNavigationProps {
  onBeforeNavigate?: () => Promise<void>;
}

export const ReportNavigation: FunctionComponent<ReportNavigationProps> = ({ onBeforeNavigate }) => {
  const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
  const location = useLocation();
  const navigate = useNavigate();
  const { errorSections, hiddenMenuItems, reportSections } = useIncidentReportContext();
  const menuItems = reportSections.filter(s => !hiddenMenuItems.includes(s.labelKey));
  const { sectionName, rootPath } = getSectionInfo(location.pathname, menuItems);

  const handleSectionClick = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    section: ReportMenuItem,
  ) => {
    e.preventDefault();
    await onBeforeNavigate?.();
    navigate(`${rootPath}/${section.url}`);
  };

  return translationsReady && (
    <div className="incident_report_navigation">
      <h3>{t('form_navigation')}</h3>
      <ul className="incident_report_menu">
        {menuItems.map(section => {
          return (
            <li
              className={clsx({
                incident_report_menu_item: true,
                active: sectionName == section.url || (sectionName === 'report' && section.url === 'intervention-summary'),
                error: errorSections?.includes(section.labelKey as ReportNavigationKey)
              })}
              key={section.labelKey}
            >
              <Link to={`${rootPath}/${section.url}`} onClick={(e) => handleSectionClick(e, section)}>
                {t(section.labelKey)}
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  );
};

interface ReportNavigationBlockerProps {
  formik: FormikProps<any>;
  onSave: () => Promise<void>;
}

export const ReportNavigationBlocker: FunctionComponent<ReportNavigationBlockerProps> = ({ formik, onSave }) => {
  const { t } = useTranslation('criticalIncident', { useSuspense: false });
  const location = useLocation();
  const navigate = useNavigate();
  const { hiddenMenuItems, reportSections } = useIncidentReportContext();
  const { registerGuard } = useNavigationGuard();
  const isConfirming = useRef(false);

  // Resolves `true` when it's OK to proceed (discard or save was chosen), `false` when the
  // dialog was dismissed via its close icon without a choice - in that case nothing is saved
  // or discarded, and the caller (link navigation or an external guarded action) must not proceed.
  const confirmBeforeLeaving = useCallback(async (): Promise<boolean> => {
    if (!formik.dirty) return true;
    if (isConfirming.current) return false;

    isConfirming.current = true;
    const choice = await confirm({
      title: t('discard_changes_title'),
      message: t('discard_changes_text'),
      confirmLabel: t('discard'),
      cancelLabel: t('save_progress'),
    });
    isConfirming.current = false;

    if (choice === undefined) return false;
    if (choice) {
      formik.resetForm();
    } else {
      await onSave();
    }
    return true;
  }, [formik, onSave, t]);

  useEffect(() => registerGuard(confirmBeforeLeaving), [registerGuard, confirmBeforeLeaving]);

  useEffect(() => {
    const menuItems = reportSections.filter(s => !hiddenMenuItems.includes(s.labelKey));
    const { rootPath } = getSectionInfo(location.pathname, menuItems);

    const handleDocumentClick = (e: MouseEvent) => {
      if (!formik.dirty || isConfirming.current) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = (e.target as HTMLElement)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.target === '_blank') return;

      const destination = new URL(anchor.href, window.location.origin);
      if (destination.origin !== window.location.origin || destination.pathname.startsWith(rootPath)) return;

      e.preventDefault();
      const to = `${destination.pathname}${destination.search}${destination.hash}`;

      confirmBeforeLeaving().then((shouldProceed) => {
        if (shouldProceed) navigate(to);
      });
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, [formik, hiddenMenuItems, reportSections, location.pathname, navigate, confirmBeforeLeaving]);

  return null;
};
